import {
  Form, FormButton, FormButtonContainer, FormItem, PageDefaultSegmentWithHeader,
  TextInput, SelectInput, isMobileView, useFeedback, useForm, OptionProps
} from "@sis/react-library"
import { useNavigate } from "react-router-dom";
import { callMimicApi } from "../../../utils/commonUtils";
import { useEffect, useState } from "react";
import { CityTownDto } from "../../sampleData/sampleDataReducer";
import { useSampleData } from "../../sampleData/useSampleData";
import { Alert } from "@mui/material";

// allow no members for shorter name to be more readable below
// eslint-disable-next-line
interface T extends CityTownDto { }

interface ListAndDetailSampleEditFormProps {
  cityTownCode?: string;
  listUrl: string;
  viewOnly?: boolean;
}

export const ListAndDetailSampleEditForm = ({
  cityTownCode, listUrl, viewOnly
}: ListAndDetailSampleEditFormProps) => {

  const formName = 'listAndDetailSampleForm';
  const navigate = useNavigate();
  const isMobile = isMobileView();
  const { pristine, initializeForm } = useForm<T>(formName);
  const { showError } = useFeedback();

  const { cityTowns, countries, populationSizes, updateCityTown } = useSampleData();

  const [submitting, setSubmitting] = useState(false);

  const forNew = cityTownCode === undefined;

  const cityTown: CityTownDto | undefined = forNew ?
    undefined :
    cityTowns.find(ct => ct.code === cityTownCode);

  // validCityTown below means it found the city/town, or it's new. 
  // (the URL could have an invalid city/town in it - edit the url to test that the alert shows that it can't find the city/town)
  const validCityTown = forNew || (!forNew && cityTown);

  //////////////////////////////////////////////////////////////////////////////////////////////
  // Because this component isn't always removed when selecting a different cityTown or new,
  // (you can keep the edit form open and select a different city), the initial values
  //  wont' be updated - so we have to manually initialize the form based on the new values.
  //////////////////////////////////////////////////////////////////////////////////////////////
  useEffect(() => {
    if (validCityTown) { initializeForm(cityTown) }
    // Only for cityTownCode (meaning the page changed)
    // eslint-disable-next-line   
  }, [cityTownCode])

  const handleSave = async (values: T) => {
    setSubmitting(true)
    try {
      await callMimicApi();
      await updateCityTown(values);
    } catch (ex) {
      showError(`Error while saving: ${ex}`);
      return false;
    } finally {
      setSubmitting(false)
    }
  }

  /////////////////////////////////////////////////////////////////////////////////////////
  // If you do not wish to close the form on save, then don't handle the after save event
  /////////////////////////////////////////////////////////////////////////////////////////
  const handleAfterSave = async () => {
    returnToList();
  }

  const returnToList = () => {
    navigate(listUrl);
  }

  const countryOptions: OptionProps[] = countries.map(c => ({
    value: c.code,
    description: c.description
  }));

  const populationSizeOptions: OptionProps[] = populationSizes.map(c => ({
    value: c.code,
    description: c.description
  }));

  const headingText = forNew ?
    'New' :
    `${viewOnly ? 'View' : 'Edit'}: ${cityTownCode}`

  return (
    <PageDefaultSegmentWithHeader
      headingText={headingText}
      standardWidth
    >
      {validCityTown &&
        <Form<T>
          name={formName}
          onSubmit={handleSave}
          onAfterSubmit={handleAfterSave}
          submitting={submitting}
          disableForm={viewOnly}
          beforeLeaveText='You have made changes to this city/town, are you sure you want to leave ?'
          initialValues={cityTown}
        >
          <FormItem<T>
            formField="code"
            label="Code"
            noFeedback={!forNew}
            rules={[{ type: "required", message: "Please enter a city / town code!" }]}
            element={<TextInput autoFocus={!isMobile && forNew} maxLength={3} disabled={!forNew} />}
          />
          <FormItem<T>
            formField="description"
            label="Description"
            rules={[{ type: "required", message: "Please enter a city/country description!" }]}
            element={<TextInput autoFocus={!isMobile && !forNew} maxLength={50} />}
          />
          <FormItem<T>
            formField="countryCode"
            label="Country"
            rules={[{ type: "required", message: "Please select a country!" }]}
            element={<SelectInput options={countryOptions} />}
          />
          <FormItem<T>
            formField="populationSizeCode"
            label="Population Size"
            rules={[{ type: "required", message: "Please select a population size!" }]}
            element={<SelectInput options={populationSizeOptions} />}
          />
          <FormButtonContainer>
            <FormButton
              formButtonText={pristine ? 'Close' : 'Cancel'}
              enabledOnPristine  // This means that the button is always enabled, even if no changes have been made
              onClick={returnToList}
            />
            {!viewOnly &&
              <FormButton
                formButtonText={forNew ? 'Create' : 'Update'}
                submitButton
              />
            }
          </FormButtonContainer>
        </Form>
      }
      {!validCityTown &&
        <Alert severity="error">City/Town code '{cityTownCode}' not found.</Alert>
      }
    </PageDefaultSegmentWithHeader>
  )
}
